import React, { useEffect, useRef, useState } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import styles from "./Dashboard.module.scss";
import { Card, EmptyState, PageTitle, TextInput } from "src/components";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRangePicker } from "react-date-range";
import "./Dashboard.scss";

import {
  BiMoney,
  BiBox,
  BiUser,
  BiBarChartSquare,
  BiGroup,
  BiSidebar,
  BiReceipt,
  BiUserCheck,
  BiTrendingUp,
  BiCalendar,
  BiPieChart,
  BiPackage,
  BiCart,
} from "react-icons/bi";

import { IoCalendarOutline } from "react-icons/io5";
import { FiFilter } from "react-icons/fi";

import Axios from "src/services/api";
import { formatCurrency } from "src/services/currency";
import numeral from "numeral";
import { prepareChartData } from "./utils";
import { Link } from "react-router-dom";
import { Countries } from "../Customers/Customers";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";
import { IoChevronDownOutline } from "react-icons/io5";
import { makeCdnUrl } from "src/services/cdn";
import { Product } from "src/models/product";
import { X } from "react-feather";

const allStatsDefault = [
  {
    title: "Visitors",
    id: "visitors",
    value: 0,
    icon: <BiUser size={28} />,
    color: "#dcf1ff",
    type: "number",
  },
  {
    title: "Conversion Rate",
    id: "conversionRate",
    value: "0%",
    icon: <BiBarChartSquare size={28} />,
    color: "#ffe5e5",
    type: "percentage",
  },
  {
    title: "Total Page Views",
    id: "pageViews",
    value: "0",
    icon: <BiSidebar size={28} />,
    color: "#ffefe0",
    type: "number",
  },
];

const statsDefault = [
  {
    title: "Revenue",
    id: "sales",
    value: "0",
    icon: <BiMoney size={28} />,
    color: "#dcffdb",
    type: "currency",
  },
  {
    title: "Orders",
    id: "orders",
    value: 0,
    icon: <BiBox size={28} />,
    color: "#f3e2ff",
    type: "number",
  },
  {
    title: "Customers",
    id: "customers",
    value: "0",
    icon: <BiGroup size={28} />,
    color: "#d9fffd",
    type: "number",
  },
];

const subscriptionStatsDefault = [
  {
    title: "Revenue",
    id: "subscriptionRevenue",
    value: 0,
    icon: <BiTrendingUp size={28} />,
    color: "#ffecb3", // Pastel amber
    type: "currency",
  },
  {
    title: "MRR",
    id: "mrr",
    value: 0,
    icon: <BiCalendar size={28} />,
    color: "#c8e6c9", // Pastel green
    type: "currency",
  },
  {
    title: "ARR",
    id: "arr",
    value: 0,
    icon: <BiPieChart size={28} />,
    color: "#bbdefb", // Pastel blue
    type: "currency",
  },
  {
    title: "Active Subscriptions",
    id: "activeSubscriptions",
    value: 0,
    icon: <BiUserCheck size={28} />,
    color: "#e1bee7", // Pastel purple
    type: "number",
  },
  {
    title: "Paid Invoices",
    id: "subscriptionInvoices",
    value: 0,
    icon: <BiReceipt size={28} />,
    color: "#ffccbc", // Pastel deep orange
    type: "number",
  },
];

const columns = [
  "Name",
  "Price",
  "Orders",
  // "Page Views",
  // "Visitors",
  "Sales",
  "Refunds",
];
const countriesColumns = [
  "Country",
  "Customers",
  "Orders",
  // "Revenue"
];

const Dashboard = () => {
  const [state, setState] = useState([
    {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [openProduct, setOpenProduct] = useState(false);
  const [stats, setStats] = useState(statsDefault);
  const [subscriptionStats, setSubscriptionStats] = useState(
    subscriptionStatsDefault
  );
  const [allStats, setAllStats] = useState(allStatsDefault);
  const [openDate, setOpenDate] = useState(false);
  const [chartData, setChartData] = useState<any>([]);
  const [productsWithSales, setProductsWithSales] = useState<any>([]);
  const [countriesData, setCountriesData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [productId, setProductId] = useState<any>(undefined);
  const [isMobile, setIsMobile] = useState(false);

  const [countries, setCountries] = useState<Countries | null>(null);

  const [firstTime, setFirstTime] = useState(true);

  const [subscriptionSearchTerm, setSubscriptionSearchTerm] = useState("");
  const [subscriptions, setSubscriptions] = useState<Product[]>([]);
  const [subscriptionId, setSubscriptionId] = useState<any>(undefined);
  const [openSubscription, setOpenSubscription] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);

  const ref = useRef<HTMLDivElement | null>(null);
  const refProduct = useRef<HTMLDivElement | null>(null);
  const refSubscription = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    import("../../services/countries.json").then((data) => {
      setCountries(data.default as Countries);
    });
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpenDate(false);
      }
      if (
        refProduct.current &&
        !refProduct.current.contains(event.target as Node)
      ) {
        setOpenProduct(false);
      }
      if (
        refSubscription.current &&
        !refSubscription.current.contains(event.target as Node)
      ) {
        setOpenSubscription(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const accessToken = localStorage.getItem("accessToken");

  useEffect(() => {
    if (!firstTime) {
      return;
    }
    setFirstTime(false);

    Axios.post(
      "/dashboard",
      {
        startDate: state[0].startDate?.toISOString(),
        endDate: state[0]?.endDate?.toISOString(),
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    ).then((response) => {
      setChartData(
        prepareChartData(
          state[0].startDate?.toISOString(),
          state[0]?.endDate?.toISOString(),
          response.data.chartData.sales,
          response.data.chartData.visitors,
          response.data.chartData.subscriptionCustomers
        )
      );

      const sales = response.data.chartData.sales;
      const refunds = response.data.refundedSales;
      const products = response.data.products;

      setCountriesData(response.data.countryData);

      setProductsWithSales(
        products.map((product: any) => {
          const productSales = sales.filter(
            (sale: any) => sale.productId === product._id
          );
          let totalRevenue = 0;
          for (const sale of productSales) {
            totalRevenue += sale.amount / 100;
          }

          const productRefunds = refunds.filter((refund: any) => {
            return refund.productId === product._id;
          });

          let totalRefund = 0;
          for (const sale of productRefunds) {
            totalRefund += sale.amount / 100;
          }

          return {
            ...product,
            totalRevenue,
            totalRefund,
            numberOfSales: productSales.length,
          };
        })
      );

      const statsData = response.data.stats.map((stat: any) => {
        const s: any = statsDefault.find((s) => s.id === stat.id);
        return {
          ...s,
          value: stat.value,
        };
      });
      const subsData = response.data.subsStats.map((stat: any) => {
        const s: any = subscriptionStatsDefault.find((s) => s.id === stat.id);
        return {
          ...s,
          value: stat.value,
        };
      });

      const allStatsData = response.data.allStats.map((stat: any) => {
        const s: any = allStatsDefault.find((s) => s.id === stat.id);
        return {
          ...s,
          value: stat.value,
        };
      });
      setAllStats(allStatsData);
      setStats(statsData);
      setSubscriptionStats(subsData);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        toast.promise(
          Axios.post(
            "/dashboard",
            {
              startDate: state[0].startDate?.toISOString(),
              endDate: state[0]?.endDate?.toISOString(),
              productId,
              subscriptionId,
            },
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          ).then((response) => {
            setChartData(
              prepareChartData(
                state[0].startDate?.toISOString(),
                state[0]?.endDate?.toISOString(),
                response.data.chartData.sales,
                response.data.chartData.visitors,
                response.data.chartData.subscriptionCustomers
              )
            );

            const sales = response.data.chartData.sales;
            const refunds = response.data.refundedSales;
            const products = response.data.products;

            setCountriesData(response.data.countryData);

            setProductsWithSales(
              products.map((product: any) => {
                const productSales = sales.filter(
                  (sale: any) => sale.productId === product._id
                );
                let totalRevenue = 0;
                for (const sale of productSales) {
                  totalRevenue += sale.amount / 100;
                }

                const productRefunds = refunds.filter((refund: any) => {
                  return refund.productId === product._id;
                });

                let totalRefund = 0;
                for (const sale of productRefunds) {
                  totalRefund += sale.amount / 100;
                }

                return {
                  ...product,
                  totalRevenue,
                  totalRefund,
                  numberOfSales: productSales.length,
                };
              })
            );

            const statsData = response.data.stats.map((stat: any) => {
              const s: any = statsDefault.find((s) => s.id === stat.id);
              return {
                ...s,
                value: stat.value,
              };
            });
            const subsData = response.data.subsStats.map((stat: any) => {
              const s: any = subscriptionStatsDefault.find(
                (s) => s.id === stat.id
              );
              return {
                ...s,
                value: stat.value,
              };
            });

            const allStatsData = response.data.allStats.map((stat: any) => {
              const s: any = allStatsDefault.find((s) => s.id === stat.id);
              return {
                ...s,
                value: stat.value,
              };
            });

            setStats(statsData);
            setSubscriptionStats(subsData);
            setAllStats(allStatsData);
          }),
          {
            loading: "Loading...",
            success: "Data loaded!",
            error: "Error when loading data",
          }
        );
      } catch (error) {
        console.error(error);
      }
    };

    if (
      ((state[0].startDate && state[0].endDate) ||
        (!state[0].startDate && !state[0].endDate)) &&
      !firstTime
    ) {
      fetchStats();
    }
  }, [state[0].startDate, state[0].endDate, productId, subscriptionId]);

  useEffect(() => {
    setLoadingProducts(true);
    Axios.get<Product[]>("/products")
      .then((response) => {
        setProducts(response.data);
        setLoadingProducts(false);
      })
      .catch(() => {
        setLoadingProducts(false);
      });
  }, []);

  useEffect(() => {
    setLoadingSubscriptions(true);
    Axios.get<Product[]>("/subscriptions")
      .then((response) => {
        setSubscriptions(response.data);
        setLoadingSubscriptions(false);
      })
      .catch(() => {
        setLoadingSubscriptions(false);
      });
  }, []);

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredSubscriptions = subscriptions.filter((subscription) =>
    subscription.name
      .toLowerCase()
      .includes(subscriptionSearchTerm.toLowerCase())
  );

  // Add effect to detect mobile devices
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Navigation for Mobile and Desktop
  const renderMobileNavbar = () => {
    return (
      <div className={styles.mobileNavbar}>
        <div className={styles.productsFilter}>
          <div
            className={styles.productsToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenProduct(!openProduct);
            }}
            title="Products filter"
          >
            <BiPackage size={24} />
          </div>
          {openProduct && (
            <div className={styles.productsListContainer} ref={refProduct}>
              <div className={styles.form}>
                <div className={styles.label}>
                  {productId ? (
                    <div className={styles.selectedItem}>
                      <span>Selected: </span>
                      {products.find((product) => product._id === productId)?.name}
                      <X
                        onClick={() => setProductId(null)}
                        size={16}
                        className={styles.clearIcon}
                      />
                    </div>
                  ) : (
                    "Filter by products"
                  )}
                </div>
                <TextInput
                  name="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  type="text"
                />
              </div>
              <div className={styles.productsFilterList}>
                {loadingProducts ? (
                  <div className={styles.loadingState}>
                    Loading products...
                  </div>
                ) : products.length === 0 ? (
                  <div className={styles.emptyState}>
                    No products available.
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className={styles.emptySearchState}>
                    No products match your search.
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <div
                      className={styles.productsFilterItem}
                      onClick={() => {
                        setProductId(product._id);
                        setOpenProduct(false);
                      }}
                      key={product._id}
                    >
                      <div className={styles.productNameContainer}>
                        <img
                          src={
                            product.thumbnail
                              ? makeCdnUrl(product.thumbnail)
                              : "https://dummyimage.com/1200&text=Product+Image"
                          }
                          alt={product.name}
                        />
                        <div className={styles.productName}>{product.name}</div>
                      </div>
                      <div className={styles.productPrice}>
                        {formatCurrency(product.price)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.subscriptionsFilter}>
          <div
            className={styles.subscriptionsToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenSubscription(!openSubscription);
            }}
            title="Subscriptions filter"
          >
            <BiReceipt size={24} />
          </div>
          {openSubscription && (
            <div className={styles.subscriptionsListContainer} ref={refSubscription}>
              <div className={styles.form}>
                <div className={styles.label}>
                  {subscriptionId ? (
                    <div className={styles.selectedItem}>
                      <span>Selected: </span>
                      {subscriptions.find((s) => s._id === subscriptionId)?.name}
                      <X
                        onClick={() => setSubscriptionId(null)}
                        size={16}
                        className={styles.clearIcon}
                      />
                    </div>
                  ) : (
                    "Filter by subscriptions"
                  )}
                </div>
                <TextInput
                  name="searchSubscription"
                  value={subscriptionSearchTerm}
                  onChange={(e) => setSubscriptionSearchTerm(e.target.value)}
                  placeholder="Search subscriptions..."
                  type="text"
                />
              </div>
              <div className={styles.subscriptionsFilterList}>
                {loadingSubscriptions ? (
                  <div className={styles.loadingState}>
                    Loading subscriptions...
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className={styles.emptyState}>
                    No subscriptions available.
                  </div>
                ) : filteredSubscriptions.length === 0 ? (
                  <div className={styles.emptySearchState}>
                    No subscriptions match your search.
                  </div>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <div
                      className={styles.subscriptionsFilterItem}
                      onClick={() => {
                        setSubscriptionId(subscription._id);
                        setOpenSubscription(false);
                      }}
                      key={subscription._id}
                    >
                      <div className={styles.subscriptionNameContainer}>
                        <img
                          src={
                            subscription.thumbnail
                              ? makeCdnUrl(subscription.thumbnail)
                              : "https://dummyimage.com/1200&text=Subscription+Image"
                          }
                          alt={subscription.name}
                        />
                        <div className={styles.subscriptionName}>
                          {subscription.name}
                        </div>
                      </div>
                      <div className={styles.subscriptionPrice}>
                        {formatCurrency(subscription.price)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.datesFilter}>
          <div
            className={styles.dateToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDate(!openDate);
            }}
            title="Date range"
          >
            <IoCalendarOutline size={24} />
          </div>
          {openDate && (
            <div className={styles.datePicker} ref={ref}>
              <DateRangePicker
                // @ts-ignore
                onChange={(item) => setState([item.selection])}
                // @ts-ignore
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={1}
                // @ts-ignore
                ranges={state}
                direction="vertical"
                inputRanges={[]}
                staticRanges={[
                  {
                    label: "Today",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "This Week",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().setDate(
                          new Date().getDate() - new Date().getDay()
                        )
                      ),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "This Month",
                    isSelected: () => true,
                    range: () => ({
                      startDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth(),
                        1
                      ),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "Last Month",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() - 1,
                        1
                      ),
                      endDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth(),
                        0
                      ),
                    }),
                  },
                ]}
              />
            </div>
          )}
        </div>

        <div className={styles.filtersButton}>
          <div
            className={styles.filtersToggler}
            onClick={() =>
              toast.success(
                "Active filters: " +
                (productId ? "Product filter, " : "") +
                (subscriptionId ? "Subscription filter, " : "") +
                "Date: " +
                dayjs(state[0].startDate).format("MMM D") +
                " - " +
                dayjs(state[0].endDate).format("MMM D")
              )
            }
            title="View active filters"
          >
            <FiFilter size={24} />
          </div>
        </div>
      </div>
    );
  };

  const renderDesktopNavbar = () => {
    return (
      <div className={styles.actions}>
        <div className={styles.productsFilter}>
          <div
            className={styles.productsToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenProduct(!openProduct);
            }}
          >
            <span>
              {!productId
                ? "All products"
                : products.find((product) => product._id === productId)?.name}{" "}
            </span>
            <div className={styles.iconContainer}>
              {productId && (
                <X
                  onClick={(e) => {
                    e.stopPropagation();
                    setProductId(null); // Clear the selected product
                  }}
                  size={18}
                  className={styles.clearIcon}
                />
              )}
              <IoChevronDownOutline
                style={{
                  marginLeft: 5,
                }}
                size={14}
              />
            </div>
          </div>
          {openProduct && (
            <div
              className={styles.productsListContainer}
              ref={refProduct}
            >
              <div className={styles.form}>
                <div className={styles.label}>Filter by products</div>
                <TextInput
                  name="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  type="text"
                />
              </div>
              <div className={styles.productsFilterList}>
                {loadingProducts ? (
                  <div className={styles.loadingState}>
                    Loading products...
                  </div>
                ) : products.length === 0 ? (
                  <div className={styles.emptyState}>
                    No products available.
                  </div>
                ) : filteredProducts.length === 0 ? (
                  <div className={styles.emptySearchState}>
                    No products match your search.
                  </div>
                ) : (
                  filteredProducts.map((product) => (
                    <div
                      className={styles.productsFilterItem}
                      onClick={() => {
                        setProductId(product._id);
                      }}
                      key={product._id}
                    >
                      <div className={styles.productNameContainer}>
                        <img
                          src={
                            product.thumbnail
                              ? makeCdnUrl(product.thumbnail)
                              : "https://dummyimage.com/1200&text=Product+Image"
                          }
                          alt={product.name}
                        />
                        <div className={styles.productName}>
                          {product.name}
                        </div>
                      </div>
                      <div className={styles.productPrice}>
                        {formatCurrency(product.price)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.subscriptionsFilter}>
          <div
            className={styles.subscriptionsToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenSubscription(!openSubscription);
            }}
          >
            <span>
              {!subscriptionId
                ? "All subscriptions"
                : subscriptions.find((s) => s._id === subscriptionId)
                  ?.name}
            </span>
            <div className={styles.iconContainer}>
              {subscriptionId && (
                <X
                  onClick={(e) => {
                    e.stopPropagation();
                    setSubscriptionId(null); // Clear selected subscription
                  }}
                  size={18}
                  className={styles.clearIcon}
                />
              )}
              <IoChevronDownOutline style={{ marginLeft: 5 }} size={14} />
            </div>
          </div>
          {openSubscription && (
            <div
              className={styles.subscriptionsListContainer}
              ref={refSubscription}
            >
              <div className={styles.form}>
                <div className={styles.label}>
                  Filter by subscriptions
                </div>
                <TextInput
                  name="searchSubscription"
                  value={subscriptionSearchTerm}
                  onChange={(e) =>
                    setSubscriptionSearchTerm(e.target.value)
                  }
                  placeholder="Search subscriptions..."
                  type="text"
                />
              </div>
              <div className={styles.subscriptionsFilterList}>
                {loadingSubscriptions ? (
                  <div className={styles.loadingState}>
                    Loading subscriptions...
                  </div>
                ) : subscriptions.length === 0 ? (
                  <div className={styles.emptyState}>
                    No subscriptions available.
                  </div>
                ) : filteredSubscriptions.length === 0 ? (
                  <div className={styles.emptySearchState}>
                    No subscriptions match your search.
                  </div>
                ) : (
                  filteredSubscriptions.map((subscription) => (
                    <div
                      className={styles.subscriptionsFilterItem}
                      onClick={() => {
                        setSubscriptionId(subscription._id);
                        setOpenSubscription(false);
                      }}
                      key={subscription._id}
                    >
                      <div className={styles.subscriptionNameContainer}>
                        <img
                          src={
                            subscription.thumbnail
                              ? makeCdnUrl(subscription.thumbnail)
                              : "https://dummyimage.com/1200&text=Subscription+Image"
                          }
                          alt={subscription.name}
                        />
                        <div className={styles.subscriptionName}>
                          {subscription.name}
                        </div>
                      </div>
                      <div className={styles.subscriptionPrice}>
                        {formatCurrency(subscription.price)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.datesFilter}>
          <div
            className={styles.dateToggler}
            onClick={(e) => {
              e.stopPropagation();
              setOpenDate(!openDate);
            }}
          >
            {dayjs(state[0].startDate).format("MMM D, YYYY")} -{" "}
            {dayjs(state[0].endDate).format("MMM D, YYYY")}
            <IoChevronDownOutline
              style={{
                marginLeft: 5,
              }}
              size={14}
            />
          </div>
          {openDate && (
            <div className={styles.datePicker} ref={ref}>
              <DateRangePicker
                // @ts-ignore
                onChange={(item) => setState([item.selection])}
                // @ts-ignore
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={2}
                // @ts-ignore
                ranges={state}
                direction="horizontal"
                inputRanges={[]}
                staticRanges={[
                  {
                    label: "Today",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "Yesterday",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().setDate(new Date().getDate() - 1)
                      ),
                      endDate: new Date(
                        new Date().setDate(new Date().getDate() - 1)
                      ),
                    }),
                  },
                  {
                    label: "This Week",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().setDate(
                          new Date().getDate() - new Date().getDay()
                        )
                      ),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "Last Week",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().setDate(
                          new Date().getDate() - new Date().getDay() - 7
                        )
                      ),
                      endDate: new Date(
                        new Date().setDate(
                          new Date().getDate() - new Date().getDay() - 1
                        )
                      ),
                    }),
                  },
                  {
                    label: "This Month",
                    isSelected: () => true,
                    range: () => ({
                      startDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth(),
                        1
                      ),
                      endDate: new Date(),
                    }),
                  },
                  {
                    label: "Last Month",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() - 1,
                        1
                      ),
                      endDate: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth(),
                        0
                      ),
                    }),
                  },
                  {
                    label: "All Time",
                    isSelected: () => false,
                    range: () => ({
                      startDate: new Date(0),
                      endDate: new Date(),
                    }),
                  },
                ]}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pageContainer}>
      <PageTitle
        actions={
          <div>
            {isMobile ? renderMobileNavbar() : renderDesktopNavbar()}
          </div>
        }
      >
        Dashboard
      </PageTitle>
      {loading ? (
        <EmptyState loading={true} />
      ) : (
        <>
          <div className={styles.topContainer}>
            <div>
              <div className={styles.gridTitle}>General stats</div>
              <div className={styles.topGrid}>
                {allStats.map((stat) => (
                  <div className={styles.statItem} key={stat.id}>
                    <div className={styles.stat}>
                      <div
                        className={styles.icon}
                        style={{
                          backgroundColor: stat.color,
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div className={styles.textSide}>
                        <div className={styles.statTitle}>{stat.title}</div>
                        <div className={styles.statValue}>
                          {stat.type === "currency"
                            ? formatCurrency(stat.value as number)
                            : stat.type === "percentage"
                              ? `${numeral(
                                (stat.value as number) > 100 ? 100 : stat.value
                              ).format("0[.]00")}%`
                              : stat.type === "number"
                                ? numeral(stat.value).format("0[.]00")
                                : stat.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className={styles.gridTitle}>One-time products</div>
              <div className={styles.topGrid}>
                {stats.map((stat) => (
                  <div className={styles.statItem} key={stat.id}>
                    <div className={styles.stat}>
                      <div
                        className={styles.icon}
                        style={{
                          backgroundColor: stat.color,
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div className={styles.textSide}>
                        <div className={styles.statTitle}>{stat.title}</div>
                        <div className={styles.statValue}>
                          {stat.type === "currency"
                            ? formatCurrency(stat.value as number)
                            : stat.type === "percentage"
                              ? `${numeral(
                                (stat.value as number) > 100 ? 100 : stat.value
                              ).format("0[.]00")}%`
                              : stat.type === "number"
                                ? numeral(stat.value).format("0[.]00")
                                : stat.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className={styles.gridTitle}>Subscriptions</div>
              <div className={styles.topGrid}>
                {subscriptionStats.filter((stat) => stat.id !== "subscriptionInvoices").map((stat) => (
                  <div className={styles.statItem} key={stat.id}>
                    <div className={styles.stat}>
                      <div
                        className={styles.icon}
                        style={{
                          backgroundColor: stat.color,
                        }}
                      >
                        {stat.icon}
                      </div>
                      <div className={styles.textSide}>
                        <div className={styles.statTitle}>{stat.title}</div>
                        <div className={styles.statValue}>
                          {stat.type === "currency"
                            ? formatCurrency(stat.value as number)
                            : stat.type === "percentage"
                              ? `${numeral(
                                (stat.value as number) > 100 ? 100 : stat.value
                              ).format("0[.]00")}%`
                              : stat.type === "number"
                                ? numeral(stat.value).format("0[.]00")
                                : stat.value}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className={styles.chartTitle}>Performance overview</div>
          <div className={styles.chartContainer}>
            {chartData && chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={isMobile ? 300 : 400} aspect={isMobile ? undefined : 3}>
                <ComposedChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 30,
                    left: isMobile ? 0 : 20,
                  }}
                >
                  <XAxis dataKey="date" />
                  <Tooltip content={<CustomTooltip />} />
                  <CartesianGrid strokeDasharray="4 5" horizontal={false} />

                  <Bar
                    dataKey="orders"
                    label="Orders"
                    yAxisId="right"
                    barSize={20}
                    stackId="a"
                    fill="#9807ff"
                  />
                  <Bar
                    dataKey="visitors"
                    label="Visitors"
                    yAxisId="right"
                    barSize={20}
                    stackId="a"
                    fill="#42adf4"
                  />
                  <Bar
                    dataKey="subscriptions"
                    yAxisId="right"
                    barSize={20}
                    stackId="a"
                    fill="#ff9800"
                    shape={<Rectangle radius={[4, 4, 0, 0]} />}
                  />
                  <Line type="monotone" dataKey="sales" stroke="#39b637" />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>No data available</div>
            )}
          </div>
          <Card>
            <div className={styles.cardTitle}>Top Selling Products</div>
            {productsWithSales?.filter((prod: any) => prod.numberOfSales > 0)
              ?.length === 0 ? (
              <div className={styles.noData}>No data</div>
            ) : (
              <div className={styles.tableResponsiveWrapper}>
                <table className={styles.productsTable}>
                  <thead>
                    <tr>
                      {columns.map((column, index) => (
                        <th key={index}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {productsWithSales
                      ?.filter((prod: any) => prod.numberOfSales > 0)
                      ?.map((product: any, index: any) => (
                        <tr key={index}>
                          <td>
                            <Link
                              className={styles.productNameContainer}
                              to={`/products/${product._id}`}
                            >
                              <img
                                src={
                                  product.thumbnail
                                    ? makeCdnUrl(product.thumbnail)
                                    : "https://dummyimage.com/1200&text=Product+Image"
                                }
                                alt={product.name}
                              />
                              <span className={styles.productName}>{product.name}</span>
                            </Link>
                          </td>
                          <td>{formatCurrency(product.price)}</td>
                          <td>{numeral(product.numberOfSales).format()}</td>
                          <td>{formatCurrency(product.totalRevenue)}</td>
                          <td>{formatCurrency(product.totalRefund)}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
          <Card>
            <div className={styles.cardTitle}>Top Countries</div>
            {!countriesData || countriesData?.length === 0 ? (
              <div className={styles.noData} role="status" aria-live="polite">No data available</div>
            ) : (
              <div className={styles.tableResponsiveWrapper}>
                <table className={styles.productsTable} aria-label="Top countries by sales and customers">
                  <thead>
                    <tr>
                      {countriesColumns.map((column, index) => (
                        <th key={index} scope="col" style={{
                          width: index === 0 ? '50%' : '25%'
                        }}>{column}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {countriesData?.map((country: any, index: any) => {
                      const cntr = countries?.[country.country];
                      return (
                        <tr key={index}>
                          <td>
                            <div className={styles.country}>
                              <div
                                className={styles.countryFlag}
                                title={`${cntr?.name} flag`}
                                aria-label={`${cntr?.name} flag`}
                                role="img"
                              >
                                {cntr?.emoji}
                              </div>
                              <span className={styles.productName} title={cntr?.name || ''}>
                                {cntr?.name}
                              </span>
                            </div>
                          </td>
                          <td aria-label="Customer Count">{numeral(country.customerCount).format()}</td>
                          <td aria-label="Sales Count">{numeral(country.salesCount).format()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="label">{`Date: ${label}`}</p>
        <ul className="tooltip-list">
          {payload.map((entry: any, index: any) => (
            <li key={`item-${index}`}>
              <span
                className="data-marker"
                style={{ backgroundColor: entry.color }}
              ></span>
              <span className="name">{entry.name}:</span>
              <span className="value">
                {entry.name === "sales"
                  ? formatCurrency(entry.value)
                  : entry.name === "visitors"
                    ? numeral(entry.value).format()
                    : numeral(entry.value).format()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return null;
};
